// Sidebar.jsx
import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  Avatar,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../AuthContext";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CalculateIcon from "@mui/icons-material/Calculate";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupsIcon from "@mui/icons-material/Groups";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TableChartIcon from "@mui/icons-material/TableChart";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import UploadIcon from "@mui/icons-material/Upload";
import { apiGet, checkMaintenanceOnce } from "../../api";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useTranslation } from "react-i18next";
import AppSidebar from "../../design-system/components/AppSidebar";
import useToast from "../../components/useToast";
import { isDemoMode } from "../../demoMode";
import { getReportingAvailability } from "../reporting/reportingFeatures";
import "../../i18n";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./sidebar.css";
import { isEmployeeAccessLevel } from "../../utils/accessLevels";

const normalizeCompanyName = (value) =>
  (value || "").trim().toLowerCase().replace(/\s+/g, " ");

/** Single source of truth for nav items */
const NAV = [
  // Dashboard/Stempeluhr laufen ohne Übertitel. Der geschützte Leerraum hält die
  // Titelzeile auf ihrer Höhe, damit die Einträge auf gleicher Linie starten wie
  // in den Gruppen darunter — AppSidebar rendert die Zeile nur, wenn title gesetzt ist.
  { type: "section", key: "sec-dashboard", title: "\u00A0", roles: ["Super_Admin", "Admin", "company_admin"] },
  { key: "dashboard-admin", labelKey: "sidebar.Dashboard", to: "/admin/dashboard", icon: <HomeOutlinedIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.admin_dashboard" },
  // { key: "dashboard-user",  ... }    // ENTFERNT, User sehen keinen Dashboard-Eintrag

  // Zeiterfassung nur für Admins (User sollen nur Shiftplan/Report/FAQ sehen)
  { key: "punch", labelKey: "sidebar.punch_clock", to: "/punch_clock", icon: <AccessTimeIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.punch_clock" },

  // Planung: Schichtplan bleibt für alle sichtbar, der Rest nur für Admins
  { type: "section", key: "sec-planning", labelKey: "sidebar.planning" },
  { key: "shiftplan",    labelKey: "sidebar.scheduler",      to: "/shiftplan",    icon: <CalendarTodayOutlinedIcon />, featureKey: "page.shiftplan" },
  { key: "planning",     labelKey: "sidebar.Planung",        to: "/demand",       icon: <EventNoteIcon />,      roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.planning" },
  // Mitarbeitende sehen hier ihre eigene Verfügbarkeit als Kalender.
  { key: "availability", labelKey: "sidebar.Verfügbarkeit",  to: "/availability", icon: <EventAvailableIcon />, roles: ["Super_Admin", "Admin", "company_admin", "User"], featureKey: "page.availability" },
  { key: "projects",     labelKey: "sidebar.Projects",       to: "/projects",     icon: <CreateNewFolderIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.expense_management" },
  // Mitarbeitende: Stempeluhr in der Planung, nur mit Zusatzmodul Stempeluhr
  // (feature_special "punch_clock" - dieselbe Bedingung wie "Erfasste Arbeitszeit").
  { key: "punch_employee", labelKey: "sidebar.punch_clock", to: "/punch_clock", icon: <AccessTimeIcon />, roles: ["User"], reportingOption: "punchClock" },

  // Team nur für Admins
  { type: "section", key: "sec-team", labelKey: "sidebar.Team", roles: ["Super_Admin", "Admin", "company_admin"] },
  { key: "team",     labelKey: "sidebar.Manage Team", to: "/team",   icon: <GroupsIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.team" },
  { key: "approval", labelKey: "sidebar.approval",    to: "/approval/flow", icon: <LibraryAddCheckIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.approval_flow" },

  // Auswertungen: Stempeluhr und Schichtplan für alle (je nach Firmeneinstellung), der Rest nur für Admins
  { type: "section", key: "sec-report", labelKey: "sidebar.analytics" },
  { key: "report_punch_clock", labelKey: "sidebar.report_punch_clock", to: "/report/punch-clock", icon: <TableChartIcon />, reportingOption: "punchClock" },
  { key: "report_shiftplan", labelKey: "sidebar.report_shiftplan", to: "/report/shiftplan", icon: <TableChartIcon />, featureKey: "page.reporting", reportingOption: "shiftplan" },
  { key: "expense_report", labelKey: "sidebar.report_expense", to: "/report/expense", icon: <TableChartIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.expense_management" },
  {
    key: "innotab_invoices",
    labelKey: "Invoices",
    to: "/report/invoices",
    icon: <ReceiptLongIcon />,
    roles: ["Super_Admin", "Admin", "company_admin"],
    showIf: (user) => normalizeCompanyName(user?.companyName) === "innotab ag",
  },
  {
    key: "billing_overview",
    labelKey: "Billing",
    to: "/report/billing",
    icon: <ReceiptLongIcon />,
    roles: ["Super_Admin", "Admin", "company_admin"],
    showIf: (user) => normalizeCompanyName(user?.companyName) === "innotab ag",
  },
  { key: "labor_costs", labelKey: "sidebar.labor_costs", to: "/report/labor-costs", icon: <AnalyticsIcon />, roles: ["Super_Admin", "Admin", "company_admin"], featureKey: "page.reporting", showIf: (_user, ctx) => !!ctx?.hasHourlyWage },

  // Admin-Aktionen nur für Super Admin
  { type: "section", key: "sec-admin-actions", labelKey: "sidebar.others", roles: ["Super_Admin"] },
  { key: "design_system",     labelKey: "Design System",   icon: <PaletteOutlinedIcon />, roles: ["Super_Admin"], to: "/design-system" },
  { key: "feature_setup",     labelKey: "Feature Setup",    icon: <SettingsIcon />,      roles: ["Super_Admin"], to: "/feature/setup", },
  { key: "solver_admin",      labelKey: "Solver NBs",       icon: <CalculateIcon />,     roles: ["Super_Admin"], to: "/admin/solver", },
  { key: "mass_upload",        labelKey: "Mass Upload",       icon: <UploadIcon />,        roles: ["Super_Admin"], onClick: "openUploadDialog" },
  { key: "duplicate_company",  labelKey: "Firma duplizieren", icon: <ContentCopyIcon />,   roles: ["Super_Admin"], onClick: "duplicateCompany" },
  { key: "delete_company",     labelKey: "Firma löschen",     icon: <DeleteForeverIcon />, roles: ["Super_Admin"], onClick: "deleteCompany" },
];

const DEMO_NAV_KEYS = new Set([
  "dashboard-admin",
  "punch",
  "availability",
  "planning",
  "shiftplan",
  "team",
  "approval",
  "report_shiftplan",
]);


const canShow = (item, user, featureWeb, ctx = {}) => {
  if (ctx.demoMode && !DEMO_NAV_KEYS.has(item.key)) return false;
  if (item.roles && !item.roles.includes(user?.accessLevel)) return false;
  if (
    item.reportingOption &&
    ctx.reportingAvailability?.[item.reportingOption] === false
  ) return false;
  if (
    user?.accessLevel !== "Super_Admin" &&
    item.featureKey &&
    Object.prototype.hasOwnProperty.call(featureWeb, item.featureKey) &&
    !featureWeb[item.featureKey]
  ) return false;
  if (item.showIf && !item.showIf(user, ctx)) return false;
  return true;
};
const isPathActive = (pathname, to) => pathname === to || pathname.startsWith(to + "/");
const getBestActiveKey = (pathname, user, featureWeb, ctx = {}) => {
  let best = null;
  for (const item of NAV) {
    if (!item.to) continue;
    if (!canShow(item, user, featureWeb, ctx)) continue;
    const itemPath = item.to;
    if (!isPathActive(pathname, itemPath)) continue;
    if (!best || itemPath.length > best.to.length) best = { ...item, to: itemPath };
  }
  return best?.key || null;
};

const Sidebar = () => {
  const { logout, selectedLocationId, user: authUser } = useContext(AuthContext);
  // Mitarbeitende: keine Planungs-Abfragen. Genehmigungen sind für User am
  // Server gesperrt (app_only meldet die Sitzung ab), Personalkosten ebenso.
  const isEmployee = isEmployeeAccessLevel(authUser?.access_level);
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const demoMode = isDemoMode();

  const token = localStorage.getItem("session_token");
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingCompany, setDeletingCompany] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateCompanyName, setDuplicateCompanyName] = useState("");
  const [duplicateError, setDuplicateError] = useState("");
  const [duplicatingCompany, setDuplicatingCompany] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState(null);

  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    accessLevel: "",
    avatarUrl: "",
  });

  const [approvalCount, setApprovalCount] = useState(0);
  const [featureWeb, setFeatureWeb] = useState({});
  const [reportingAvailability, setReportingAvailability] = useState(() =>
    getReportingAvailability([])
  );
  const [hasHourlyWage, setHasHourlyWage] = useState(false);
  const [availabilityView, setAvailabilityView] = useState(true);

  // --- Fetch current user (unchanged) ---
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await apiGet("/api/web/current_user", logout);
        if (!data) return;
        await checkMaintenanceOnce();
        setUser({
          firstName: data.first_name,
          lastName: data.last_name,
          companyName: data.company_name,
          email: data.email,
          accessLevel: data.access_level,
          avatarUrl: data.avatar_url || "",
        });
        setAvailabilityView(data?.availability_view !== false);
      } catch (error) {
        console.error("❌ Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [logout]);

  // --- Track active route (unchanged) ---
  useEffect(() => {
    const activeKey = getBestActiveKey(location.pathname, user, featureWeb, {
      hasHourlyWage,
      demoMode,
      reportingAvailability,
    });
    if (activeKey) {
      localStorage.setItem("selectedSidebarItem", activeKey);
    } else {
      localStorage.removeItem("selectedSidebarItem");
    }
  }, [location.pathname, user, featureWeb, hasHourlyWage, demoMode, reportingAvailability]);

  const onFileChange = (event) => setFile(event.target.files[0]);

  const onFileUpload = () => {
    const formData = new FormData();
    formData.append("file", file);
    axios
      .post(`${API_BASE_URL}/api/web/mass-upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setMessage(response.data.success || "Upload processed.");
        setOpen(false);
      })
      .catch((error) => {
        setMessage(error?.response?.data?.error || "Upload failed");
      });
  };

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleDeleteDialogOpen = () => {
    setDeletePassword("");
    setDeleteError("");
    setDeleteOpen(true);
  };
  const handleDeleteDialogClose = () => {
    if (deletingCompany) return;
    setDeleteOpen(false);
    setDeletePassword("");
    setDeleteError("");
  };
  const handleDuplicateDialogOpen = () => {
    setDuplicateCompanyName(`${user.companyName || ""} Copy`.trim());
    setDuplicateError("");
    setDuplicateResult(null);
    setDuplicateOpen(true);
  };
  const handleDuplicateDialogClose = () => {
    if (duplicatingCompany) return;
    setDuplicateOpen(false);
    setDuplicateError("");
    setDuplicateResult(null);
  };
  const handleCopyLoginEmail = async () => {
    const loginEmail = duplicateResult?.login_email;
    if (!loginEmail) return;
    try {
      await navigator.clipboard.writeText(loginEmail);
      toast.success(t("sidebar_dialogs.duplicate_company.login_copied"));
    } catch {
      toast.error(t("sidebar_dialogs.duplicate_company.login_copy_failed"));
    }
  };
  const handleDeleteCompany = async () => {
    const password = deletePassword.trim();
    if (!password) {
      setDeleteError(t("sidebar_dialogs.delete_company.password_required"));
      return;
    }

    setDeletingCompany(true);
    setDeleteError("");
    try {
      await axios.post(
        `${API_BASE_URL}/api/web/settings/delete`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeleteOpen(false);
      setDeletePassword("");
      logout();
    } catch (error) {
      setDeleteError(
        error?.response?.data?.error || t("sidebar_dialogs.delete_company.failed")
      );
    } finally {
      setDeletingCompany(false);
    }
  };
  const handleDuplicateCompany = async () => {
    const newCompanyName = duplicateCompanyName.trim();

    if (!newCompanyName) {
      setDuplicateError(t("sidebar_dialogs.duplicate_company.name_required"));
      return;
    }

    setDuplicatingCompany(true);
    setDuplicateError("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/web/settings/duplicate`,
        { new_company_name: newCompanyName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Der Dialog bleibt offen und zeigt die Login-Adresse der Kopie — das ist
      // der naechste Schritt, und sonst muesste sie aus der neuen ID abgeleitet
      // werden.
      setDuplicateResult(response?.data || null);
      toast.success(
        t("sidebar_dialogs.duplicate_company.created_toast", { name: newCompanyName })
      );
    } catch (error) {
      setDuplicateError(
        error?.response?.data?.error || t("sidebar_dialogs.duplicate_company.failed")
      );
    } finally {
      setDuplicatingCompany(false);
    }
  };

  // initials fallback
  const initials =
    (user.firstName?.[0] || "").toUpperCase() + (user.lastName?.[0] || "").toUpperCase();

  useEffect(() => {
    const fetchFeatureWeb = async () => {
      try {
        const [data, punchClockFeature] = await Promise.all([
          apiGet("/api/web/feature-web", logout),
          apiGet("/api/web/feature-special/punch_clock", logout),
        ]);
        const enabledByKey = {};
        (Array.isArray(data) ? data : []).forEach((item) => {
          const key = item?.feature_key;
          if (key) enabledByKey[key] = !!item?.enabled;
        });
        setFeatureWeb(enabledByKey);
        setReportingAvailability(getReportingAvailability(data, punchClockFeature?.enabled));
      } catch {
        setFeatureWeb({});
        setReportingAvailability(getReportingAvailability([]));
      }
    };
    fetchFeatureWeb();
  }, [logout]);

  useEffect(() => {
    let active = true;

    if (isEmployee) return undefined;

    const fetchHourlyWageVisibility = async () => {
      try {
        const data = await apiGet("/api/report/labor_costs/visibility", logout);
        if (active) setHasHourlyWage(Boolean(data?.has_hourly_wage));
      } catch {
        if (active) setHasHourlyWage(false);
      }
    };

    fetchHourlyWageVisibility();
    return () => {
      active = false;
    };
  }, [isEmployee, logout]);

  useEffect(() => {
    let active = true;

    if (isEmployee) return undefined;

    const fetchApprovalCount = async () => {
      try {
        const data = await apiGet("/api/web/approval/count", logout);
        if (active) setApprovalCount(Math.max(0, Number(data?.count) || 0));
      } catch {
        if (active) setApprovalCount(0);
      }
    };

    fetchApprovalCount();
    const intervalId = window.setInterval(fetchApprovalCount, 60_000);
    window.addEventListener("approvals:changed", fetchApprovalCount);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("approvals:changed", fetchApprovalCount);
    };
  }, [isEmployee, logout, selectedLocationId]);

  const ctx = { hasHourlyWage, demoMode, reportingAvailability };
  const activeKey = getBestActiveKey(location.pathname, user, featureWeb, ctx);
  const sidebarGroups = [];
  let currentGroup = null;

  NAV.forEach((item) => {
    if (item.type === "section") {
      currentGroup = {
        key: item.key,
        // Sektionen ohne labelKey bringen ihren Titel selbst mit (siehe sec-dashboard).
        title: item.labelKey ? (t(item.labelKey) || item.labelKey) : item.title,
        items: [],
      };
      sidebarGroups.push(currentGroup);
      return;
    }

    if (!canShow(item, user, featureWeb, ctx)) return;

    if (!currentGroup) {
      currentGroup = { key: "main", items: [] };
      sidebarGroups.push(currentGroup);
    }

    const itemLabel = item.key === "availability"
      ? (availabilityView ? t("sidebar.Verfügbarkeit") : t("sidebar.Abwesenheit"))
      : (t(item.labelKey) || item.labelKey);
    const badge = item.key === "approval" && approvalCount > 0
      ? (approvalCount > 99 ? "99+" : approvalCount)
      : undefined;

    currentGroup.items.push({
      key: item.key,
      label: itemLabel,
      icon: item.icon,
      to: item.to,
      action: item.onClick,
      badge,
      badgeLabel: badge ? `${badge} offene Genehmigungen` : undefined,
    });
  });

  const visibleSidebarGroups = sidebarGroups.filter((group) => group.items.length > 0);

  const handleSidebarItemClick = (item) => {
    if (item.to) {
      navigate(item.to);
      return;
    }

    if (item.action === "openUploadDialog") handleClickOpen();
    if (item.action === "duplicateCompany") handleDuplicateDialogOpen();
    if (item.action === "deleteCompany") handleDeleteDialogOpen();
  };

  // DESKTOP
  return (
    <Box sx={{ width: "var(--ds-app-shell-sidebar-width-collapsed)", flexShrink: 0 }}>
      <AppSidebar
        logoSrc="/assets/TimeTab_Logo_quer_cmyk_pos.svg"
        logoAlt="TimeTab"
        faviconSrc="/assets/TimeTab_Favicon.png"
        groups={visibleSidebarGroups}
        activeItemKey={activeKey}
        onItemClick={handleSidebarItemClick}
        profile={{
          icon: (
            <Avatar src={user.avatarUrl || undefined}>
              {initials || <PersonOutlinedIcon fontSize="small" />}
            </Avatar>
          ),
          name: `${user.firstName} ${user.lastName}`.trim() || "User",
          role: user.companyName,
          onClick: () => navigate("/settings"),
        }}
      />

      {/* Mass Upload Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t("sidebar_dialogs.mass_upload.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("sidebar_dialogs.mass_upload.description")}
          </DialogContentText>
          <input type="file" onChange={onFileChange} style={{ display: "none" }} id="upload-file" />
          <label htmlFor="upload-file" style={{ cursor: "pointer" }}>
            <Button variant="contained" component="span">
              {t("sidebar_dialogs.mass_upload.choose_file")}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("button.cancel")}</Button>
          <Button onClick={onFileUpload} disabled={!file}>
            {t("sidebar_dialogs.mass_upload.upload")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>{t("sidebar_dialogs.delete_company.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("sidebar_dialogs.delete_company.description")}
          </DialogContentText>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("sidebar_dialogs.delete_company.password_prompt")}
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            type="password"
            label={t("login.password")}
            value={deletePassword}
            onChange={(e) => {
              setDeletePassword(e.target.value);
              if (deleteError) setDeleteError("");
            }}
            error={!!deleteError}
            helperText={deleteError || " "}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !deletingCompany) handleDeleteCompany();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} disabled={deletingCompany}>{t("button.cancel")}</Button>
          <Button color="error" onClick={handleDeleteCompany} disabled={deletingCompany || !deletePassword.trim()}>
            {deletingCompany ? t("sidebar_dialogs.delete_company.deleting") : t("button.delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={duplicateOpen} onClose={handleDuplicateDialogClose} fullWidth maxWidth="xs">
        <DialogTitle>
          {duplicateResult
            ? t("sidebar_dialogs.duplicate_company.done_title")
            : t("sidebar_dialogs.duplicate_company.title")}
        </DialogTitle>
        {duplicateResult ? (
          <>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                {t("sidebar_dialogs.duplicate_company.done_description")}
              </DialogContentText>
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("sidebar_dialogs.duplicate_company.done_company")}
                  </Typography>
                  <Typography>{duplicateResult.company_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("sidebar_dialogs.duplicate_company.done_schema")}
                  </Typography>
                  <Typography sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                    {duplicateResult.schema_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("sidebar_dialogs.duplicate_company.done_login")}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                      {duplicateResult.login_email}
                    </Typography>
                    <Button size="small" onClick={handleCopyLoginEmail}>
                      {t("sidebar_dialogs.duplicate_company.copy")}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t("sidebar_dialogs.duplicate_company.done_password_hint")}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDuplicateDialogClose}>{t("button.close")}</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <DialogContentText sx={{ mb: 1.5 }}>
                {t("sidebar_dialogs.duplicate_company.description")}
              </DialogContentText>
              <TextField
                fullWidth
                autoFocus
                margin="dense"
                label={t("sidebar_dialogs.duplicate_company.name")}
                value={duplicateCompanyName}
                onChange={(e) => {
                  setDuplicateCompanyName(e.target.value);
                  if (duplicateError) setDuplicateError("");
                }}
                error={!!duplicateError}
                helperText={duplicateError || " "}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !duplicatingCompany) handleDuplicateCompany();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDuplicateDialogClose} disabled={duplicatingCompany}>{t("button.cancel")}</Button>
              <Button
                onClick={handleDuplicateCompany}
                disabled={duplicatingCompany || !duplicateCompanyName.trim()}
              >
                {duplicatingCompany ? t("sidebar_dialogs.duplicate_company.duplicating") : t("sidebar_dialogs.duplicate_company.duplicate")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {message && <Typography color="error">{message}</Typography>}
    </Box>
  );
};

export default Sidebar;
